import { twMerge } from 'tailwind-merge'
import { useUserLevelingProgressionQuery } from '../api'

/**
 * The username sitting on its own level-progress bar, as in the original design: the gradient fill
 * is progress toward the next level, with the name and level reading over it.
 *
 * `level_progress` is a fraction, not a percentage — the model documents it as "0.0 to 1.0", the
 * Django admin renders it with a `:.2%` format, and the dashboard bar multiplies by 100. Treating
 * it as a percentage would peg this bar at ~1% for everyone.
 *
 * Falls back to a plain name when there is no level to show. The query declares `initialData` with
 * level 0, so `data` is defined and `isSuccess` true from the first render; gating on a loading
 * flag would flash an empty bar at every user on every page load.
 */
export const UserLevelBar = ({ username, className }: { username: string; className?: string }) => {
    const { data } = useUserLevelingProgressionQuery()
    const level = data?.level ?? 0

    if (level <= 0) return <span className='text-sm uppercase font-bold'>{username}</span>

    const progress = Math.min(100, Math.max(0, (data?.level_progress ?? 0) * 100))

    return (
        <span
            role='progressbar'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-label={`Level ${level}, ${Math.round(progress)}% to next level`}
            title={`Level ${level}: ${data?.current_exp ?? 0} / ${data?.next_level_exp ?? 0} XP`}
            className={twMerge(
                'relative flex flex-row items-center gap-2 overflow-hidden min-w-[9rem] h-8 px-3 rounded-sm',
                'bg-[#2b1b40] border border-white/10',
                // An underline across a progress bar reads as a mistake. The bar lifts instead:
                // the rim brightens and a brand-tinted glow comes up under it. Driven by the
                // surrounding menu trigger as well as the bar itself, so the whole control
                // responds as one object.
                'transition-[border-color,box-shadow] duration-200',
                'hover:border-white/40 hover:shadow-[0_0_14px_rgba(201,31,255,0.45)]',
                'group-hover/usermenu:border-white/40 group-hover/usermenu:shadow-[0_0_14px_rgba(201,31,255,0.45)]',
                className
            )}
        >
            <span
                aria-hidden='true'
                className='absolute inset-y-0 left-0 bg-br1-gradient transition-[width] duration-700'
                style={{ width: `${progress}%` }}
            />
            {/* Sheen across the whole track, so the unfilled remainder lights up too. */}
            <span
                aria-hidden='true'
                className='absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-200 group-hover/usermenu:opacity-100'
            />
            <span className='relative text-sm uppercase font-bold truncate'>{username}</span>
            <span className='relative text-2xs uppercase tracking-wide text-white/70 whitespace-nowrap'>
                Lvl {level}
            </span>
        </span>
    )
}
